import React, { useState, useEffect } from 'react';
import PostSendModal from '../PostSendModal/PostSendModal';
import cn from 'classnames';
import { Button } from 'components/UI';
import { Icon, Markup } from '@landing/ui';
import { defaultModalsId } from '../constants';
import { observer, modal as modalStore } from 'store';
import {
  VALID_SPECIALIZATIONS_FOR_TRAINEE,
  DEV_SPECIALIZATIONS,
  NOT_DEV_SPECIALIZATIONS,
} from './constants';
import * as styles from './TraineeSuccessModal.module.scss';

function TraineeSuccessModal({
  title = 'Thanks for your application form!',
  subtitle = 'We have sent further instructions to ',
  link = 'Go to the website',
  extendedTitle = 'Your next steps:',
  extendedText = '<li>Send a message with your first name, last name, traineeship area, and location in our Telegram chat.</li><li>Make sure to turn your Telegram notifications on to receive updates about recruitment and trainee program details.</li><li>Stay tuned for messages in the Telegram chat with important information related to your application.</li>',
  extendedTextBeforeEmail = 'The information above is duplicated to ',
  extendedLink = 'Telegram chat',
}) {
  const isOpen = modalStore.modalID === defaultModalsId.traineeSuccessModal;
  const [modalType, setModalType] = useState(null);
  const [telegramLink, setTelegramLink] = useState(null);
  const props = modalStore.modalProps;
  const getIcon = () => <Icon name="testing" className={cn(styles.icon, 'svg-icon')} />;
  const countryOptions = [
    {
      content: 'Azerbaijan',
      value: 'Azerbaijan',
      countryCode: 'AZ',
    },
  ];
  const preparedCountries = countryOptions?.filter(
    ({ value }) => !value.toLowerCase().startsWith('other'),
  );
  const countriesForExtendedText = preparedCountries?.map(({ value }) => value.toLowerCase()) ?? [];
  const showExtendedText = countriesForExtendedText.some(
    country => country === props.country?.toLowerCase(),
  );

  useEffect(() => {
    if (!isOpen) return;
    if (showExtendedText) {
      const checkSingleSpecialization =
        props.specializations?.length === 1 &&
        VALID_SPECIALIZATIONS_FOR_TRAINEE.includes(props.specializations[0]);
      const validSpecializations = props.specializations?.length > 1 || checkSingleSpecialization;
      const showExtendedTextForTrainee = !props.course && validSpecializations;

      if (props.course || showExtendedTextForTrainee) {
        setTelegramLink(prepareTelegramLink(props.specializations, Boolean(props.course)));
        setModalType('extended');
        return;
      }
    }
    setModalType('default');
  }, [
    isOpen,
    props.course,
    props.specializations,
    props.country,
    countriesForExtendedText,
    VALID_SPECIALIZATIONS_FOR_TRAINEE,
  ]);

  return modalType ? (
    <PostSendModal
      Icon={getIcon}
      modalID={defaultModalsId.traineeSuccessModal}
      theme="traineeInvitationSend"
    >
      <p className={styles.text}>{title}</p>
      {modalType === 'extended' ? (
        <>
          <div className={styles.extendedContent}>
            <p className={styles.extendedTitle}>{extendedTitle}</p>
            <Markup tag="ol" className={styles.extendedText}>
              {extendedText}
            </Markup>
            <p className={styles.subtext}>
              {extendedTextBeforeEmail}
              <span className={styles.email}>{props.email}</span>
            </p>
          </div>
          {telegramLink && (
            <Button tag="a" href={telegramLink} onClick={modalStore.close} fullWidthMobile>
              {extendedLink}
            </Button>
          )}
        </>
      ) : (
        <>
          <p className={styles.subtext}>
            {subtitle}
            <span className={styles.email}>{props.email}</span>
          </p>
          <Button tag="a" href="/" target="_self" onClick={modalStore.close} fullWidthMobile>
            {link}
          </Button>
        </>
      )}
    </PostSendModal>
  ) : null;
}

export default observer(TraineeSuccessModal);

const prepareTelegramLink = (specs, isCourse) => {
  if (isCourse) {
    return 'https://t.me/andersen_trainee';
  }

  if (specs.length > 1 || (specs.length === 1 && DEV_SPECIALIZATIONS.includes(specs[0]))) {
    return 'https://t.me/andersen_dev_trainee';
  }

  if (specs.length === 1 && NOT_DEV_SPECIALIZATIONS.includes(specs[0])) {
    return 'https://t.me/andersen_tbd_trainee';
  }

  return 'https://t.me/andersen_trainee';
};
